// 2026-05-26 - FormuLearning - UI 상태 관리 (Zustand)
// 2026-09-21 수정 - 이슈 #77(G6): 새 프로젝트를 만들 폴더(newProjectFolderId) — 폴더 우클릭 «New Project Here»
// 파일 위치: frontend/src/stores/uiStore.ts

import { create } from 'zustand'
import { type Theme, getInitialTheme, applyTheme } from '../lib/theme'

type ModalType = 'project-new' | 'project-load' | 'project-info' | null
type SyncStatus = 'idle' | 'saving' | 'synced'

// 2026-07-09 - 이슈 #33 4단계: Autosave 토글. ON=증분 자동저장, OFF=Save 시 일괄. localStorage 영속.
const AUTOSAVE_KEY = 'formulearn.autosave'
function getInitialAutosave(): boolean {
  try {
    return localStorage.getItem(AUTOSAVE_KEY) !== 'false' // 기본 true(미설정·오류 시 ON)
  } catch {
    return true
  }
}

interface UiState {
  leftPanelOpen: boolean
  activeModal: ModalType
  activeStep: number
  syncStatus: SyncStatus
  selectedProjectId: string | null
  newProjectFolderId: string | null
  theme: Theme
  autosave: boolean
  toggleLeftPanel: () => void
  openModal: (modal: ModalType) => void
  closeModal: () => void
  setActiveStep: (step: number) => void
  setSyncStatus: (s: SyncStatus) => void
  setSelectedProjectId: (id: string | null) => void
  openNewProject: (folderId: string | null) => void
  toggleTheme: () => void
  setTheme: (t: Theme) => void
  setAutosave: (v: boolean) => void
}

export const useUiStore = create<UiState>((set) => ({
  leftPanelOpen: true,
  activeModal: null,
  activeStep: 1,
  syncStatus: 'idle',
  selectedProjectId: null,
  newProjectFolderId: null,
  theme: getInitialTheme(),
  autosave: getInitialAutosave(),
  toggleLeftPanel: () => set((s) => ({ leftPanelOpen: !s.leftPanelOpen })),
  openModal: (modal) => set({ activeModal: modal }),
  closeModal: () => set({ activeModal: null, selectedProjectId: null, newProjectFolderId: null }),
  setActiveStep: (step) => set({ activeStep: step }),
  setSyncStatus: (syncStatus) => set({ syncStatus }),
  setSelectedProjectId: (id) => set({ selectedProjectId: id }),
  // 이슈 #77: null = 루트. 폴더 위에서 열면 그 폴더에 만든다(REQ-UX-31).
  openNewProject: (folderId) => set({ activeModal: 'project-new', newProjectFolderId: folderId }),
  // 2026-06-19 - 테마 순환 (이슈 #9): dark → light → mixed → dark. data-theme·localStorage 반영
  toggleTheme: () => set((s) => {
    const order: Theme[] = ['dark', 'light', 'mixed']
    const next = order[(order.indexOf(s.theme) + 1) % order.length]
    applyTheme(next)
    return { theme: next }
  }),
  setTheme: (t) => { applyTheme(t); set({ theme: t }) },
  // 2026-07-09 - 이슈 #33 4단계: Autosave 토글 지속(localStorage).
  setAutosave: (v) => {
    try { localStorage.setItem(AUTOSAVE_KEY, v ? 'true' : 'false') } catch { /* 무시 */ }
    set({ autosave: v })
  },
}))
